import { DragEscapeGuard } from 'src/modules/shared/components/atoms/drag-escape-guard';
import { nonEmptyString } from 'src/modules/shared/utils/formatting/non-empty-string.util';
import { useEffect, useMemo, useState } from 'react';
import {
  CustomFieldEntityType,
  type CustomFieldDefinition,
} from 'src/api/generated';
import {
  useCustomFieldDefinitions,
  useDeleteCustomFieldDefinition,
} from 'src/modules/shared/hooks/api/custom-fields/definitions';
import { Text } from 'src/modules/shared/components/atoms/text';
import { EmptyState } from 'src/modules/shared/components/atoms/states/empty-state';
import { ErrorState } from 'src/modules/shared/components/atoms/states/error-state';
import { CardSkeleton } from 'src/modules/shared/components/atoms/cards/card-skeleton';
import { ConfirmModal } from 'src/modules/shared/components/organisms/modal';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  type DragEndEvent,
  type DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers';

import { DraggableItem } from 'src/modules/shared/components/molecules/draggable-item';
import CustomFieldDefinitionCard from '../../molecules/custom-fields/custom-field-definition-card';
import { useReorderFunnelCustomFieldDefinitions } from 'src/modules/shared/hooks/api/crm/funnels/funnel-custom-field-definitions';
import { useAlert } from 'src/contexts/alert-context';
import { PiInfo, PiListBullets } from 'react-icons/pi';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import { CrmCustomFieldPermission } from 'src/modules/shared/types/permissions.type';
import { useRemoveDefinitionFunnel } from 'src/modules/shared/hooks/api/crm/funnels/custom-field-definition-funnels';
import { useFunnels } from 'src/modules/shared/hooks/api/crm/funnels/funnels';
import { DefinitionWithFunnels } from './definition-with-funnels';

interface CustomFieldDefinitionsListProps {
  entityType?: CustomFieldEntityType;
  onEdit: (definitionId: number) => void;
  ids?: number[];
  definitions?: CustomFieldDefinition[];
  funnelId?: number;
}

export const CustomFieldDefinitionsList: React.FC<
  CustomFieldDefinitionsListProps
> = ({
  entityType,
  onEdit,
  ids,
  definitions: definitionsOverride,
  funnelId,
}) => {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [definitionToDelete, setDefinitionToDelete] =
    useState<CustomFieldDefinition | null>(null);
  const [forceDelete, setForceDelete] = useState(false);
  const [activeId, setActiveId] = useState<number | null>(null);
  const { showAlert } = useAlert();

  const { hasPermission: canUpdate } = useHasPermissions(
    CrmCustomFieldPermission.Update
  );
  const { hasPermission: canDelete } = useHasPermissions(
    CrmCustomFieldPermission.Delete
  );

  const {
    definitions: fetchedDefinitions,
    loading: fetchedLoading,
    error: fetchedError,
    refetch,
  } = useCustomFieldDefinitions({
    entityType,
    ids,
    enabled: !definitionsOverride,
  });

  const definitions = useMemo(
    () =>
      (definitionsOverride ?? fetchedDefinitions).filter(
        (definition) => definition.entityType !== CustomFieldEntityType.PROPERTY
      ),
    [definitionsOverride, fetchedDefinitions]
  );
  const [orderedDefinitions, setOrderedDefinitions] =
    useState<CustomFieldDefinition[]>(definitions);
  useEffect(() => {
    setOrderedDefinitions(definitions);
  }, [definitions]);
  const loading = definitionsOverride ? false : fetchedLoading;
  const error = definitionsOverride ? null : fetchedError;

  const isSortable = Boolean(funnelId);

  const { reorder } = useReorderFunnelCustomFieldDefinitions(funnelId, {
    onSuccess: () => {
      // TODO: check when implementing i18n
      showAlert('El nuevo orden ha sido guardado.', 'success');
    },
    onError: () => {
      // TODO: check when implementing i18n
      showAlert(
        'No se pudo guardar el nuevo orden. Inténtalo de nuevo.',
        'error'
      );
    },
  });

  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 8 },
  });
  const sensors = useSensors(pointerSensor);

  const handleDragStart = (event: DragStartEvent) => {
    const itemPrefix = 'item-';
    const id = event.active.id.toString();
    if (!id.startsWith(itemPrefix)) return;
    setActiveId(Number(id.slice(itemPrefix.length)));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const itemPrefix = 'item-';
    const activeId = active.id.toString();
    const overId = over.id.toString();
    if (!activeId.startsWith(itemPrefix) || !overId.startsWith(itemPrefix)) {
      return;
    }
    const activeItemId = Number(activeId.slice(itemPrefix.length));
    const overItemId = Number(overId.slice(itemPrefix.length));
    const oldIndex = orderedDefinitions.findIndex((d) => d.id === activeItemId);
    const newIndex = orderedDefinitions.findIndex((d) => d.id === overItemId);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(orderedDefinitions, oldIndex, newIndex);
    setOrderedDefinitions(reordered);
    const orderedDefinitionIds = reordered.map((d) => d.id);
    void reorder(orderedDefinitionIds);
    setActiveId(null);
  };

  const { deleteDefinition, loading: deleting } =
    useDeleteCustomFieldDefinition({
      onSuccess: () => {
        setDeleteModalOpen(false);
        setDefinitionToDelete(null);
        setForceDelete(false);
      },
    });

  const { removeFunnel, loading: removingMapping } = useRemoveDefinitionFunnel(
    definitionToDelete?.id,
    {
      onSuccess: () => {
        setDeleteModalOpen(false);
        setDefinitionToDelete(null);
        setForceDelete(false);
      },
    }
  );

  const handleRemoveMapping = async (definitionId: number) => {
    if (!funnelId) return;

    try {
      await removeFunnel({ funnelId, definitionIdOverride: definitionId });
    } catch (error) {
      console.error('Error removing funnel mapping:', error);
    }
  };

  const handleDeleteClick = (definition: CustomFieldDefinition) => {
    setDefinitionToDelete(definition);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!definitionToDelete) return;

    if (funnelId) {
      await handleRemoveMapping(definitionToDelete.id);
      return;
    }
    try {
      await deleteDefinition({ id: definitionToDelete.id });
    } catch (error) {
      // Error handling is managed by the hook's error state; keep modal open
      console.error('Error deleting custom field definition:', error);
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setDefinitionToDelete(null);
    setForceDelete(false);
  };

  const { funnels: allFunnels = [] } = useFunnels();
  const funnelsMap = new Map<number, string>(
    allFunnels.map((f) => [f.id, f.name])
  );

  if (loading) {
    return (
      <div className="space-y-3 w-full">
        <CardSkeleton
          variant="neutral"
          header={{
            title: true,
            subtitle: true,
            action: true,
          }}
          content={{
            lines: 3,
            widths: ['w-5/6', 'w-2/3', 'w-1/2'],
            heights: ['h-4', 'h-4', 'h-4'],
          }}
        />
        <CardSkeleton
          variant="neutral"
          header={{
            title: true,
            subtitle: true,
            action: true,
          }}
          content={{
            lines: 3,
            widths: ['w-5/6', 'w-2/3', 'w-1/2'],
            heights: ['h-4', 'h-4', 'h-4'],
          }}
        />
        <CardSkeleton
          variant="neutral"
          header={{
            title: true,
            subtitle: true,
            action: true,
          }}
          content={{
            lines: 3,
            widths: ['w-5/6', 'w-2/3', 'w-1/2'],
            heights: ['h-4', 'h-4', 'h-4'],
          }}
        />
      </div>
    );
  }

  if (error) {
    return (
      <ErrorState
        // TODO: check when implementing i18n
        title="Error cargando campos personalizados"
        // TODO: check when implementing i18n
        description="No se pudieron cargar los campos personalizados. Por favor, inténtalo de nuevo."
        // TODO: check when implementing i18n
        onAction={() => refetch()}
        actionContent="Reintentar"
      />
    );
  }

  if (definitions.length === 0) {
    return (
      <EmptyState
        // TODO: check when implementing i18n
        title="No hay campos personalizados"
        // TODO: check when implementing i18n
        description="No se han creado campos personalizados aún."
        icon={<PiListBullets className="w-full h-full" />}
        placement="vertical"
      />
    );
  }

  return (
    <>
      <div className="flex flex-col">
        <div className="grid grid-cols-1 gap-4">
          {isSortable ? (
            <DndContext
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              onDragStart={handleDragStart}
              onDragCancel={() => setActiveId(null)}
              sensors={sensors}
              modifiers={[restrictToVerticalAxis, restrictToParentElement]}
            >
              <DragEscapeGuard />
              <SortableContext
                items={orderedDefinitions.map((d) => `item-${d.id}`)}
                strategy={verticalListSortingStrategy}
              >
                {orderedDefinitions.map((definition, indexInOrder) => {
                  const positionLabel = indexInOrder + 1;
                  return (
                    <DraggableItem
                      key={`item-${definition.id}`}
                      id={`item-${definition.id}`}
                      data={definition}
                      unstyled
                      className="cursor-default"
                    >
                      {({ isDragging, DragHandle }) => (
                        <CustomFieldDefinitionCard
                          definition={definition}
                          positionLabel={positionLabel}
                          DragHandle={DragHandle}
                          canUpdate={canUpdate}
                          canDelete={canDelete}
                          onEdit={onEdit}
                          onDeleteClick={handleDeleteClick}
                          overlay={isDragging}
                        />
                      )}
                    </DraggableItem>
                  );
                })}
              </SortableContext>
              <DragOverlay dropAnimation={null}>
                {activeId != null
                  ? (() => {
                      const active = orderedDefinitions.find(
                        (d) => d.id === activeId
                      );
                      if (!active) return null;
                      const positionLabel =
                        orderedDefinitions.findIndex(
                          (d) => d.id === active.id
                        ) + 1;
                      return (
                        <CustomFieldDefinitionCard
                          definition={active}
                          positionLabel={positionLabel}
                          canUpdate={canUpdate}
                          canDelete={canDelete}
                          onEdit={onEdit}
                          onDeleteClick={handleDeleteClick}
                          overlay
                        />
                      );
                    })()
                  : null}
              </DragOverlay>
            </DndContext>
          ) : (
            <>
              {definitions.map((definition, indexInOrder) => {
                const positionLabel = funnelId ? indexInOrder + 1 : undefined;
                return funnelId ? (
                  <CustomFieldDefinitionCard
                    key={definition.id}
                    definition={definition}
                    positionLabel={positionLabel}
                    canUpdate={canUpdate}
                    canDelete={canDelete}
                    onEdit={onEdit}
                    onDeleteClick={handleDeleteClick}
                  />
                ) : (
                  <DefinitionWithFunnels
                    key={definition.id}
                    definition={definition}
                    positionLabel={positionLabel}
                    canUpdate={canUpdate}
                    canDelete={canDelete}
                    onEdit={onEdit}
                    onDeleteClick={handleDeleteClick}
                    funnelsMap={funnelsMap}
                  />
                );
              })}
            </>
          )}
        </div>
      </div>

      {deleteModalOpen && definitionToDelete && (
        <ConfirmModal
          isOpen={deleteModalOpen}
          onClose={handleCancelDelete}
          // TODO: check when implementing i18n
          title={
            funnelId
              ? 'Quitar campo personalizado del embudo'
              : 'Eliminar Campo Personalizado'
          }
          size="md"
          onConfirm={handleConfirmDelete}
          // TODO: check when implementing i18n
          confirmText={
            funnelId
              ? 'Quitar del embudo'
              : forceDelete
                ? 'Eliminar Forzado'
                : 'Eliminar'
          }
          // TODO: check when implementing i18n
          cancelText="Cancelar"
          confirmDisabled={deleting || removingMapping}
          loading={deleting || removingMapping}
          variant="danger"
        >
          <div className="flex items-start gap-3">
            <PiInfo className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
            <div className="space-y-2">
              <Text size="small" className="text-gray-700">
                {/* TODO: check when implementing i18n */}
                {funnelId ? (
                  <>
                    ¿Estás seguro de que quieres quitar el campo personalizado{' '}
                    <span className="font-medium">
                      {nonEmptyString(definitionToDelete.fieldLabel) ??
                        definitionToDelete.fieldKey}
                    </span>{' '}
                    del embudo seleccionado?
                  </>
                ) : (
                  <>
                    ¿Estás seguro de que quieres eliminar el campo personalizado{' '}
                    <span className="font-medium">
                      {nonEmptyString(definitionToDelete.fieldLabel) ??
                        definitionToDelete.fieldKey}
                    </span>
                    ?
                  </>
                )}
              </Text>
              {forceDelete && !funnelId && (
                <Text
                  size="small"
                  className="text-amber-700 bg-amber-50 p-2 rounded"
                >
                  {/* TODO: check when implementing i18n */}
                  Este campo tiene datos asociados. Al eliminarlo, también se
                  eliminarán todos los valores almacenados.
                </Text>
              )}
            </div>
          </div>
        </ConfirmModal>
      )}
    </>
  );
};
